import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../../util/api";
import { createPortal } from "react-dom";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import checkLottie from "../animation/check.lottie";
import ActionButton from "../../../component/common/ActionButton";

const API_BASE_URL =
    process.env.REACT_APP_API_REAL_URL || "http://localhost:11115/api";

const AUTO_CLOSE_SEC = 20;
const RESET_AFTER_IDLE_MS = 3000;

const LS_SECTION_KEY = "qr_attendance_section_name";

export default function AttendancePage() {
    const [hiddenValue, setHiddenValue] = useState("");
    const hiddenInputRef = useRef(null);

    const [submitting, setSubmitting] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);

    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const [remainSec, setRemainSec] = useState(AUTO_CLOSE_SEC);
    const countdownTimerRef = useRef(null);

    const [shakeKey, setShakeKey] = useState(0);

    // ✅ 카드 값이 "입력되는 중(인식중)" 상태
    const [isReading, setIsReading] = useState(false);

    // ✅ "마지막 입력 이후 3초" 리셋 타이머
    const resetIdleTimerRef = useRef(null);

    // ✅ stale closure 방지용 ref들
    const hiddenValueRef = useRef("");
    const submittingRef = useRef(false);

    // ✅ Enter 2번(리더 CR/LF) 방지용 디바운스
    const lastSubmitAtRef = useRef(0);

    // ✅ 섹션 상태
    const [sectionName, setSectionName] = useState("");
    const [sectionGateOpen, setSectionGateOpen] = useState(false);
    const sectionInputRef = useRef(null);

    const focusHiddenInput = () => hiddenInputRef.current?.focus();

    // ================= 섹션 초기 로드 =================
    useEffect(() => {
        const saved = (localStorage.getItem(LS_SECTION_KEY) || "").trim();
        if (!saved) {
            setSectionGateOpen(true);
            setTimeout(() => sectionInputRef.current?.focus(), 30);
        } else {
            setSectionName(saved);
            setTimeout(() => focusHiddenInput(), 30);
        }
    }, []);

    // ================= 클릭 포커스 유지 =================
    useEffect(() => {
        focusHiddenInput();
        const onClick = () => focusHiddenInput();
        window.addEventListener("click", onClick);
        return () => window.removeEventListener("click", onClick);
    }, []);

    // ✅ hiddenValue 최신값 ref 동기화
    useEffect(() => {
        hiddenValueRef.current = hiddenValue;
    }, [hiddenValue]);

    // ✅ submitting 최신값 ref 동기화
    useEffect(() => {
        submittingRef.current = submitting;
    }, [submitting]);

    // ✅ 모달 열려있을 때 body 스크롤 잠금
    useEffect(() => {
        if (!modalOpen && !sectionGateOpen) return;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prevOverflow;
        };
    }, [modalOpen, sectionGateOpen]);

    const clearResetTimer = () => {
        if (resetIdleTimerRef.current) {
            clearTimeout(resetIdleTimerRef.current);
            resetIdleTimerRef.current = null;
        }
    };

    const resetToIdle = () => {
        clearResetTimer();
        setHiddenValue("");
        setIsReading(false);
        setError(null);
        setTimeout(() => focusHiddenInput(), 30);
    };

    // ================= 입력값 변화 감지 (3초 리셋) =================
    useEffect(() => {
        if (submitting) return;

        const v = (hiddenValue ?? "").trim();
        const hasAnyInput = v.length > 0;

        if (!hasAnyInput) {
            setIsReading(false);
            clearResetTimer();
            return;
        }

        setIsReading(true);

        clearResetTimer();
        resetIdleTimerRef.current = setTimeout(() => {
            const latest = (hiddenValueRef.current ?? "").trim();
            if (!submittingRef.current && latest.length > 0) {
                resetToIdle();
            }
        }, RESET_AFTER_IDLE_MS);
    }, [hiddenValue, submitting]);

    /* ================= 카운트다운 ================= */
    const stopCountdown = () => {
        if (countdownTimerRef.current) {
            clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = null;
        }
    };

    const closeModalNow = () => {
        stopCountdown();
        setModalOpen(false);
        setResult(null);
        setError(null);
        setTimeout(() => focusHiddenInput(), 30);
    };

    const startCountdown = () => {
        stopCountdown();
        setRemainSec(AUTO_CLOSE_SEC);

        countdownTimerRef.current = setInterval(() => {
            setRemainSec((prev) => {
                if (prev <= 1) {
                    stopCountdown();
                    closeModalNow();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    useEffect(() => {
        return () => stopCountdown();
    }, []);

    const openModalFor = (payload) => {
        setResult(payload);
        setModalOpen(true);
        startCountdown();

        if (payload?.action && payload.action !== "ERROR") {
            setShakeKey((k) => k + 1);
        }

        setTimeout(() => focusHiddenInput(), 30);
    };

    /* ================= 날짜/시간 유틸 ================= */
    const toDate = (v) => {
        if (!v) return null;
        const d = new Date(v);
        return isNaN(d.getTime()) ? null : d;
    };
    const pad = (n) => String(n).padStart(2, "0");

    const formatKoreanTimeNoSec = (iso) => {
        const d = toDate(iso);
        if (!d) return "-";
        const h24 = d.getHours();
        const ampm = h24 < 12 ? "오전" : "오후";
        const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
        const m = d.getMinutes();
        return `${ampm} ${h12}시 ${m}분`;
    };

    const formatDate = (iso) => {
        const d = toDate(iso);
        if (!d) return "-";
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    };

    const formatStay = (startIso, endIso) => {
        const s = toDate(startIso);
        const e = toDate(endIso);
        if (!s || !e) return "-";

        let totalSec = Math.floor((e.getTime() - s.getTime()) / 1000);
        if (totalSec < 0) totalSec = 0;

        const h = Math.floor(totalSec / 3600);
        const m = Math.floor((totalSec % 3600) / 60);
        const sec = totalSec % 60;

        if (h >= 1) return `${h}시간 ${m}분 ${sec}초`;
        if (m >= 1) return `${m}분 ${sec}초`;
        return `${sec}초`;
    };

    /* ================= 중앙 상태 텍스트 ================= */
    const centerStatus = useMemo(() => {
        if (sectionGateOpen) return { title: "섹션 설정", desc: "이 기기의 섹션명을 입력해주세요." };
        if (submitting) return { title: "처리 중", desc: "서버 응답을 기다리고 있습니다." };
        if (isReading) return { title: "카드 값 인식중", desc: "카드의 데이터를 추출하고 있습니다." };
        return { title: "태그 대기중", desc: "카드를 1초간 태깅해주세요." };
    }, [submitting, isReading, sectionGateOpen]);

    /* ================= 서버 호출 ================= */
    const submitCode = async (raw) => {
        const code = (raw || "").trim();
        if (!code) return;

        const sn = (sectionName || "").trim();
        if (!sn) {
            setSectionGateOpen(true);
            setTimeout(() => sectionInputRef.current?.focus(), 30);
            return;
        }

        // ✅ 즉시 락
        if (submittingRef.current) return;
        submittingRef.current = true;

        // ✅ 서버 요청 시작 시 리셋 타이머 정리 + 인식중 해제
        setIsReading(false);
        clearResetTimer();

        setSubmitting(true);
        setError(null);
        stopCountdown();
        setModalOpen(false);
        setResult(null);

        try {
            // ✅ 컨트롤러는 AttendanceMarkRequest JSON을 받음
            const data = await api.post("/public/qr-event-participant/attendance", {
                privateCode: code,
                sectionName: sn,
            });
            openModalFor(data);
        } catch (e) {
            const msg = e?.message || "오류가 발생했습니다.";
            setError(msg);
            openModalFor({ action: "ERROR", message: msg, sectionName: sectionName });
        } finally {
            submittingRef.current = false;

            setSubmitting(false);
            setHiddenValue("");
            setTimeout(focusHiddenInput, 30);
        }
    };

    const onHiddenKeyDown = (e) => {
        if (e.key !== "Enter") return;

        e.preventDefault();
        if (e.repeat) return;
        if (submittingRef.current) return;

        const now = Date.now();
        if (now - lastSubmitAtRef.current < 250) return;
        lastSubmitAtRef.current = now;

        submitCode(hiddenValueRef.current);
    };

    const isJoin = result?.action === "JOIN";
    const isLeave = result?.action === "LEAVE";

    // ================= 섹션 저장/변경 =================
    const saveSection = () => {
        const v = (sectionName || "").trim();
        if (!v) return;
        localStorage.setItem(LS_SECTION_KEY, v);
        setSectionName(v);
        setSectionGateOpen(false);
        setTimeout(() => focusHiddenInput(), 30);
    };

    // ================= 섹션 입력 모달 =================
    const sectionGateNode =
        sectionGateOpen &&
        createPortal(
            <div className="att-modal-backdrop" onClick={() => {}}>
                <div className="att-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="att-modal-body">
                        <div className="att-modal-center">
                            <div className="att-modal-title">구역 설정</div>
                            <div className="att-modal-sub" style={{ marginBottom: 18 }}>
                                이 기기가 담당하는 구역명을 입력하세요.
                            </div>

                            <div style={{ width: "min(520px, 92vw)", textAlign: "left" }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: "#6b7280", marginBottom: 8 }}>
                                    구역명
                                </div>

                                <input
                                    ref={sectionInputRef}
                                    value={sectionName}
                                    onChange={(e) => setSectionName(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            saveSection();
                                        }
                                    }}
                                    placeholder="예) A홀 / 등록데스크 / 세션1"
                                    style={{
                                        width: "100%",
                                        padding: "14px 12px",
                                        borderRadius: 14,
                                        border: "1px solid #e5e7eb",
                                        fontSize: 18,
                                        fontWeight: 700,
                                        outline: "none",
                                    }}
                                />

                                <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                                    <ActionButton
                                        variant="primary"
                                        startIcon={false}
                                        onClick={saveSection}
                                        sx={{
                                            flex: 1,
                                            py: "14px", px: "12px",
                                            borderRadius: "14px",
                                            fontWeight: 700, fontSize: 16,
                                            bgcolor: "#16a34a",
                                            "&:hover": { bgcolor: "#15803d" },
                                        }}
                                        label="저장하고 시작"
                                    />

                                    <ActionButton
                                        variant="secondary"
                                        startIcon={false}
                                        onClick={() => {
                                            localStorage.removeItem(LS_SECTION_KEY);
                                            setSectionName("");
                                            setTimeout(() => sectionInputRef.current?.focus(), 30);
                                        }}
                                        sx={{
                                            py: "14px", px: "12px",
                                            borderRadius: "14px",
                                            border: "1px solid #e5e7eb",
                                            bgcolor: "#fff", color: "#111827",
                                            fontWeight: 700, fontSize: 16,
                                            "&:hover": { borderColor: "#e5e7eb", bgcolor: "#f9fafb", color: "#111827" },
                                        }}
                                        label="초기화"
                                    />
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </div>,
            document.body
        );

    // ================= 결과 모달 메시지 =================
    const resultSection = (result?.sectionName || sectionName || "").trim();
    const actionLabel = isJoin ? "입장" : isLeave ? "퇴장" : "";

    // ✅ 모달 JSX
    const modalNode =
        modalOpen &&
        createPortal(
            <div className="att-modal-backdrop" onClick={closeModalNow}>
                <div
                    key={`modal-${shakeKey}-${result?.action || ""}`}
                    className={`att-modal ${result?.action !== "ERROR" ? "shake-once" : ""}`}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="att-modal-body">
                        {result?.action === "ERROR" ? (
                            <div className="att-modal-center">
                                <div className="att-modal-title">오류</div>
                                <div className="att-modal-sub">{result?.message || "처리 실패"}</div>
                            </div>
                        ) : (
                            <>
                                <div className="att-modal-center">
                                    <div className="att-modal-check">
                                        <DotLottieReact src={checkLottie} autoplay loop={false} />
                                    </div>

                                    {/* ✅ 큰 타이틀: 환영합니다/감사합니다 + 이름 */}
                                    <div className="att-modal-title">
                                        {isJoin ? "환영합니다 " : "감사합니다 "}
                                        <span style={{ color: "#16a34a" }}>{result?.name}</span>님
                                    </div>

                                    {/* ✅ 아래 문구: 섹션 + 입장/퇴장 완료 */}
                                    <div className="att-modal-sub" style={{ marginTop: 12 }}>
                                        {resultSection ? (
                                            <>

                                                { isJoin ? (
                                                 <>
                                                     <b style={{ color: "#16a34a" }}>{resultSection}</b>에{" "}
                                                     <b style={{ color: "#111827" }}>{actionLabel}</b>이 완료되었습니다.
                                                 </>
                                                ) :
                                                <>
                                                    <b style={{ color: "#16a34a" }}>{resultSection}</b>에서{" "}
                                                    <b style={{ color: "#111827" }}>{actionLabel}</b>이 완료되었습니다.
                                                </>

                                                }

                                            </>
                                        ) : (
                                            <>
                                                <b style={{ color: "#111827" }}>{actionLabel}</b>이 완료되었습니다.
                                            </>
                                        )}
                                    </div>

                                    {/* ✅ LEAVE일 때만 요약카드 유지 */}
                                    {isLeave && (
                                        <div className="att-summary-card">
                                            <div className="att-grid-3">
                                                <div className="att-item">
                                                    <div className="att-item-label">입장</div>
                                                    <div className="att-item-time">{formatKoreanTimeNoSec(result?.joinedAt)}</div>
                                                    <div className="att-item-date">{formatDate(result?.joinedAt)}</div>
                                                </div>

                                                <div className="att-item">
                                                    <div className="att-item-label">퇴장(갱신)</div>
                                                    <div className="att-item-time">{formatKoreanTimeNoSec(result?.leftAt)}</div>
                                                    <div className="att-item-date">{formatDate(result?.leftAt)}</div>
                                                </div>

                                                <div className="att-item">
                                                    <div className="att-item-label">행사장 내 잔류</div>
                                                    <div className="att-item-stay">{formatStay(result?.joinedAt, result?.leftAt)}</div>
                                                    <div className="att-item-stay-sub">입장 - 퇴장(갱신)까지</div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>

                    <div className="att-modal-hint">{remainSec}초 뒤 자동으로 닫힙니다.</div>
                </div>
            </div>,
            document.body
        );

    return (
        <>
            <style>{`
        .att-kiosk-root{
          position:fixed;
          inset:0;
          width:100vw;
          height:100vh;
          background:linear-gradient(135deg,#eaf7f1,#eef4ff,#fff7e6);
          display:flex;
          align-items:center;
          justify-content:center;
          padding:18px;
          z-index:0;
        }

        .att-center-card{
          width:min(820px,95vw);
          background:rgba(255,255,255,.68);
          border:1px solid rgba(229,231,235,.9);
          border-radius:22px;
          box-shadow:0 12px 40px rgba(0,0,0,.14);
          padding:42px 30px;
          text-align:center;
          backdrop-filter:blur(10px);
        }

        .att-center-title{
          font-size:44px;
          font-weight:900;
          letter-spacing:-0.8px;
          margin-bottom:10px;
          color:#111827;
        }
        .att-center-desc{
          font-size:16px;
          font-weight:800;
          color:#374151;
          margin-bottom:10px;
        }

        .att-section-line{
          margin-bottom:18px;
          font-size:14px;
          font-weight:900;
          color:#111827;
        }
        .att-section-line b{ color:#16a34a; }
        .att-section-link{
          margin-left:10px;
          cursor:pointer;
          color:#2563eb;
          font-weight:900;
          user-select:none;
        }

        .att-dots{display:inline-flex;gap:10px;align-items:center;justify-content:center;}
        .att-dot{
          width:12px;height:12px;border-radius:999px;
          background:rgba(17,24,39,.35);
          animation:att-bounce 1.0s infinite ease-in-out;
        }
        .att-dot:nth-child(2){animation-delay:.16s;}
        .att-dot:nth-child(3){animation-delay:.32s;}
        @keyframes att-bounce{
          0%,80%,100%{transform:translateY(0);opacity:.35;}
          40%{transform:translateY(-10px);opacity:1;}
        }

        .att-hidden-input{
          position:fixed;
          left:-9999px; top:0;
          width:1px; height:1px;
          opacity:0;
        }

        .att-modal-backdrop{
          position:fixed;
          inset:0;
          background:rgba(0,0,0,.45);
          display:flex;
          align-items:center;
          justify-content:center;
          z-index:99999;
          padding:18px;
        }

        .att-modal{
          width:min(800px, 94vw);
          min-height:520px;
          max-height: calc(100vh - 48px);
          overflow:auto;
          background:#fff;
          border-radius:22px;
          border:1px solid #e5e7eb;
          box-shadow:0 18px 60px rgba(0,0,0,.22);
          padding:34px 28px 34px;
          text-align:center;
          display:flex;
          flex-direction:column;
        }

        .att-modal-body{ flex:1; display:flex; flex-direction:column; }

        .att-modal-center{
          flex:1;
          display:flex;
          flex-direction:column;
          align-items:center;
          justify-content:center;
          padding:6px 0;
        }

        .att-modal-check{ width:140px;height:140px; margin:0 auto 8px; }
        .att-modal-title{
          font-size:32px;
          font-weight:900;
          margin-top:9px;
          color:#111827;
          letter-spacing:0.2px;
        }
        .att-modal-sub{
          font-size:16px;
          font-weight:800;
          margin-top:12px;
          color:#111827;
        }

        .att-summary-card{
          margin:24px auto 0;
          width:min(680px,92vw);
          background:#f9fafb;
          border:1px solid #e5e7eb;
          border-radius:18px;
          padding:14px 14px;
          text-align:left;
        }

        .att-grid-3{
          display:grid;
          grid-template-columns:1fr 1fr 1fr;
          gap:12px;
        }
        @media (max-width:860px){
          .att-grid-3{grid-template-columns:1fr;}
          .att-summary-card{width:min(720px,94vw);}
          .att-modal{min-height:560px;}
        }

        .att-item{
          border:1px solid rgba(229,231,235,.9);
          background:#fff;
          border-radius:14px;
          padding:12px 12px;
          min-height:84px;
          display:flex;
          flex-direction:column;
          justify-content:center;
        }
        .att-item-label{
          font-size:13px;
          font-weight:900;
          color:#6b7280;
          margin-bottom:8px;
        }
        .att-item-time{
          font-size:21px;
          font-weight:900;
          color:#111827;
          letter-spacing:-0.4px;
          line-height:1.15;
          white-space:nowrap;
          overflow:hidden;
          text-overflow:ellipsis;
        }
        .att-item-date{
          margin-top:6px;
          font-size:12px;
          font-weight:800;
          color:#6b7280;
        }
        .att-item-stay{
          font-size:22px;
          font-weight:900;
          color:#111827;
          letter-spacing:-0.4px;
          line-height:1.15;
          white-space:nowrap;
          overflow:hidden;
          text-overflow:ellipsis;
        }
        .att-item-stay-sub{
          margin-top:6px;
          font-size:12px;
          font-weight:800;
          color:#6b7280;
        }

        .att-modal-hint{
          margin-top:18px;
          font-size:13px;
          font-weight:800;
          color:#6b7280;
        }

        .att-error-text{
          margin-top:14px;
          color:#b91c1c;
          font-weight:900;
          font-size:16px;
        }
      `}</style>

            <div className="att-kiosk-root">
                <div className="att-center-card" onClick={focusHiddenInput}>
                    <div className="att-center-title">{centerStatus.title}</div>
                    <div className="att-center-desc">{centerStatus.desc}</div>

                    {/* ✅ 태그 대기중일 때도 섹션이름 표시 */}
                    {!sectionGateOpen && (
                        <div className="att-section-line">
                            <b onClick={(e) => {
                                e.stopPropagation();
                                setSectionGateOpen(true);
                                setTimeout(() => sectionInputRef.current?.focus(), 30);
                            }}>{sectionName}</b>
                        </div>
                    )}

                    <div className="att-dots" aria-label="loading">
                        <span className="att-dot" />
                        <span className="att-dot" />
                        <span className="att-dot" />
                    </div>

                    {error && <div className="att-error-text">{error}</div>}

                    <input
                        ref={hiddenInputRef}
                        className="att-hidden-input"
                        value={hiddenValue}
                        onChange={(e) => setHiddenValue(e.target.value)}
                        onKeyDown={onHiddenKeyDown}
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck={false}
                    />
                </div>
            </div>

            {sectionGateNode}
            {modalNode}
        </>
    );
}